import { useEffect, useRef, useCallback } from 'react';
import { useBackend } from './useBackend';
import type { DataPackageDto } from '@/api/ApiClient';
import { log } from '@/lib/utils/logger';

interface SnapshotProgressHookReturn {
  /** Start polling for snapshot progress (numeric id, DOI, or prePublishId — same as data-package GET route). */
  startPolling: (apiId: string) => void;
  /** Stop polling for snapshot progress */
  stopPolling: () => void;
}

interface SnapshotProgressOptions {
  /** Callback when snapshot progress updates */
  onProgress?: (percent: number, inProgress: boolean) => void;
  /** Called after each successful `GET` with the latest DTO (e.g. merge snapshot fields into the tree store). */
  onPollPackage?: (dataPackage: DataPackageDto) => void;
  /** Callback when snapshot completes */
  onComplete?: () => void;
  /** Callback when an error occurs */
  onError?: (error: unknown) => void;
  /** Polling interval in milliseconds (default: 3000) */
  pollInterval?: number;
}

/**
 * Hook to poll for snapshot creation progress
 * Based on legacy SnapshotStatusUpdater.ts
 */
export function useSnapshotProgress(options: SnapshotProgressOptions = {}): SnapshotProgressHookReturn {
  const { onProgress, onPollPackage, onComplete, onError, pollInterval = 3000 } = options;
  const backend = useBackend();
  const canceledRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dataPackageApiIdRef = useRef<string | null>(null);
  const pollProgressRef = useRef<(() => Promise<void>) | undefined>(undefined);

  const stopPolling = useCallback(() => {
    canceledRef.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    dataPackageApiIdRef.current = null;
  }, []);

  const pollProgress = useCallback(async () => {
    if (canceledRef.current || !dataPackageApiIdRef.current) {
      return;
    }

    const apiId = dataPackageApiIdRef.current;
    if (!apiId) return;

    try {
      const dataPackage = await backend.data.get(apiId);
      const inProgress = dataPackage.snapshotInProgress || false;
      const percent = Math.round(dataPackage.percentSnapshotDone || 0);

      log.debug('[useSnapshotProgress] Poll result:', {
        dataPackageApiId: dataPackageApiIdRef.current,
        inProgress,
        percent,
        rawSnapshotInProgress: dataPackage.snapshotInProgress,
        rawPercentSnapshotDone: dataPackage.percentSnapshotDone,
      });

      onPollPackage?.(dataPackage);
      onProgress?.(percent, inProgress);

      if (!inProgress) {
        log.debug('[useSnapshotProgress] Snapshot complete');
        onComplete?.();
        stopPolling();
        return;
      }

      // Continue polling
      timeoutRef.current = setTimeout(() => {
        pollProgressRef.current?.();
      }, pollInterval);
    } catch (error) {
      log.error('[useSnapshotProgress] Error polling snapshot progress:', error);
      onError?.(error);
      stopPolling();
    }
  }, [backend, onProgress, onPollPackage, onComplete, onError, pollInterval, stopPolling]);

  // Update ref whenever pollProgress changes
  useEffect(() => {
    pollProgressRef.current = pollProgress;
  }, [pollProgress]);

  const startPolling = useCallback(
    (apiId: string) => {
      log.debug('[useSnapshotProgress] Starting to poll for data package:', apiId);
      stopPolling(); // Stop any existing polling
      canceledRef.current = false;
      dataPackageApiIdRef.current = apiId;
      void pollProgress();
    },
    [pollProgress, stopPolling]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    startPolling,
    stopPolling,
  };
}
